import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Eye, Package } from "lucide-react";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

const STAGES = ["material_cut", "welded", "powder_coated", "dispatched"];
const stageLabel = (prod) => {
  const done = STAGES.filter((s) => prod?.[s]).length;
  if (done === 0) return { text: "NOT STARTED", cls: "bg-yellow-600/30 text-yellow-300" };
  if (done === 4) return { text: "DISPATCHED", cls: "bg-green-600/30 text-green-300" };
  return { text: `IN PROGRESS ${done}/4`, cls: "bg-blue-600/30 text-blue-300" };
};

export const AdminOrders = ({ client, api, goTab, perms = [] }) => {
  const canMilestones = perms.includes("orders:milestones");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`${api}/qv2/orders`);
      setOrders(data);
    } catch {
      toast.error("Could not load orders");
    } finally {
      setLoading(false);
    }
  }, [client, api]);

  useEffect(() => {
    load();
  }, [load]);

  const setStageLevel = async (q, level) => {
    try {
      await Promise.all(
        STAGES.map((s, idx) =>
          client.patch(`${api}/qv2/quotations/${q.id}/production`, { stage: s, done: idx < level })
        )
      );
      toast.success("Production stage updated");
      load();
    } catch {
      toast.error("Could not update stage");
    }
  };

  const booked = orders.reduce((s, q) => s + (q.grand_total || 0), 0);
  const bookedKg = orders.reduce((s, q) => s + (q.total_kg || 0), 0);

  return (
    <div data-testid="orders-panel">
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-4">
        Confirmed orders ({orders.length}) {loading && <Loader2 size={12} className="inline animate-spin ml-2" />}
      </p>

      <div className="grid grid-cols-2 gap-3 mb-6 max-w-md">
        <div className="bg-steel border border-white/10 p-5">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Booked</p>
          <p data-testid="orders-booked-value" className="font-heading font-semibold text-2xl text-white mt-1">{fmt(booked)}</p>
        </div>
        <div className="bg-steel border border-white/10 p-5">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Weight</p>
          <p data-testid="orders-booked-kg" className="font-heading font-semibold text-2xl text-white mt-1">{bookedKg.toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg</p>
        </div>
      </div>

      {orders.length === 0 && !loading ? (
        <div data-testid="orders-empty" className="bg-steel border border-white/10 p-14 text-center">
          <Package size={40} className="mx-auto text-smoke" strokeWidth={1.5} />
          <p className="mt-4 font-mono text-xs tracking-[0.3em] uppercase text-smoke">No confirmed orders yet</p>
        </div>
      ) : (
        <div className="border border-white/10 bg-steel overflow-x-auto">
          <table data-testid="orders-table" className="w-full min-w-[860px]">
            <thead>
              <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                <th className="py-3 px-4 font-medium">Quote #</th>
                <th className="py-3 px-4 font-medium">Confirmed</th>
                <th className="py-3 px-4 font-medium">Customer</th>
                <th className="py-3 px-4 font-medium">Items</th>
                <th className="py-3 px-4 font-medium">Weight</th>
                <th className="py-3 px-4 font-medium">Order Value</th>
                <th className="py-3 px-4 font-medium">Stage</th>
                <th className="py-3 px-4 font-medium">By</th>
                <th className="py-3 px-4 font-medium text-right">View</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((q) => {
                const st = stageLabel(q.production);
                return (
                  <tr key={q.id} data-testid={`order-row-${q.quote_no}`} className="border-t border-white/10 hover:bg-ink/40 transition-colors duration-300">
                    <td className="py-3.5 px-4 font-mono text-xs text-[#7FB3E8] whitespace-nowrap">{q.quote_no}</td>
                    <td className="py-3.5 px-4 font-mono text-xs text-smoke whitespace-nowrap">{fmtDate(q.created_at)}</td>
                    <td className="py-3.5 px-4 font-heading font-semibold text-base uppercase tracking-wide text-white whitespace-nowrap">{q.customer?.company}</td>
                    <td className="py-3.5 px-4 font-mono text-xs text-smoke">{q.items_count}</td>
                    <td className="py-3.5 px-4 font-mono text-xs text-smoke">{q.total_kg}</td>
                    <td className="py-3.5 px-4 font-mono text-xs text-white whitespace-nowrap">{fmt(q.grand_total)}</td>
                    <td className="py-3.5 px-4">
                      {canMilestones ? (
                        <select
                          data-testid={`order-stage-select-${q.quote_no}`}
                          value={STAGES.filter((s) => q.production?.[s]).length}
                          onChange={(e) => setStageLevel(q, Number(e.target.value))}
                          className="bg-ink border border-white/20 font-mono text-[10px] tracking-widest uppercase text-white/80 px-2 py-1.5 focus:outline-none"
                        >
                          <option value={0}>Not Started</option>
                          <option value={1}>Material Cut</option>
                          <option value={2}>Welded</option>
                          <option value={3}>Powder Coated</option>
                          <option value={4}>Dispatched</option>
                        </select>
                      ) : (
                        <span data-testid={`order-stage-${q.quote_no}`} className={`font-mono text-[10px] tracking-widest uppercase px-2 py-1 ${st.cls}`}>{st.text}</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-xs text-smoke">{q.created_by}</td>
                    <td className="py-3.5 px-4 text-right">
                      <button data-testid={`order-view-${q.quote_no}`} onClick={() => goTab("quotes")} className="font-mono text-[10px] tracking-widest uppercase text-[#7FB3E8] hover:text-white transition-colors duration-300 px-2 py-1 inline-flex items-center gap-1">
                        <Eye size={13} /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
